import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DataGrid, { Column, DataGridRef, Editing, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import Guid from '../../models/Guid';
import IGestionCategorieUnite from "../../models/IGestionCategorieUnite";
import IUniteCategorieForfait from '../../models/IUniteCategorieForfait';
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionPlans from '../gestion-plans/gestion-plans';
import { useContextPopupPage } from '../../components/popups/PopupPage';

interface IGestionCategoriesUnitesProps {
  isModeSelection?: boolean,
}

const uniteCategorieForfaitTempId = new Guid("ffffffff-ffff-ffff-ffff-fffffffffffe");

export default function GestionCategoriesUnites({ isModeSelection }: IGestionCategoriesUnitesProps) {
  const categorieUniteDataSourceOption: IFormBaseDataSourceOptions<IGestionCategorieUnite> = useMemo(() => ({
    path: "uniteCategorie",
    customReadByIdPath: "uniteCategorie/{key}/gestion",
    customSauvegarderPath: "uniteCategorie/save",
    primaryKey: "idUniteCategorie",
  }), []);

  const clientFormDataSource = useCustomDataSource<IGestionCategorieUnite, IGestionCategorieUnite>(categorieUniteDataSourceOption);
  const {
    selectedDataRow: selectedCategorieUnite,
    onFieldDataChanged,
    setMainForm,
    setSelectedDataRow,
    mode,
    setMode,
    isFieldModifier,
    setIsFieldModifier,
  } = clientFormDataSource;

  const { editorOptions: planExclusiviteEditorOption, PopupGestion: PopupGestionPlanExclusivite } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "planentete",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des plans",
    cacheKey: "lookup-planentete-exclusivite",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idPlanExclusivite", Guid.empty.toString());
    },
  });

  const { editorOptions: planForfaitEditorOptions, PopupGestion: PopupGestionPlansForfaits } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "planentete",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des plans",
    cacheKey: "lookup-planentete-forfait-categorie",
  });

  const planForfaitGridLookupOptions = useMemo(() => {
    const { onValueChanged: _, ...lookupOptions } = planForfaitEditorOptions;
    return lookupOptions;
  }, [planForfaitEditorOptions]);

  const formRef = useRef<FormRef>(null);
  const forfaitGridRef = useRef<DataGridRef>(null);
  const skipForfaitGridSyncRemainingRef = useRef(0);
  const editingForfaitRowIndexRef = useRef(-1);
  const editingUniteCategorieForfaitRowRef = useRef<IUniteCategorieForfait | null>(null);
  const { setSelectedValue } = useContextPopupPage();
  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);

  const normalizeUniteCategorieForfaitRows = useCallback((items: IUniteCategorieForfait[]) => {
    return items.map((item) => {
      const pk = item.idUniteCategorieForfait;
      const isEmptyPk = !pk || new Guid(String(pk)).isEmpty();
      if (isEmptyPk) {
        return { ...item, idUniteCategorieForfait: uniteCategorieForfaitTempId };
      }
      return item;
    });
  }, []);

  const syncCategorieUniteForfaitRows = useCallback((
    component: { option?: (name: string) => unknown },
  ) => {
    const rawItems = component?.option?.('dataSource');
    if (!Array.isArray(rawItems)) {
      return;
    }
    const forfaits = normalizeUniteCategorieForfaitRows(rawItems as IUniteCategorieForfait[]);
    setSelectedDataRow((prev) => (prev ? { ...prev, forfaits } : prev));
    setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier, normalizeUniteCategorieForfaitRows]);

  const applyForfaitPlanFromPopup = useCallback((idPlanEntete: Guid) => {
    const editRow = editingUniteCategorieForfaitRowRef.current;
    if (!editRow) {
      return;
    }
    skipForfaitGridSyncRemainingRef.current = 3;
    setSelectedDataRow((prev) => {
      if (!prev) {
        return prev;
      }
      const forfaits = [...(prev.forfaits ?? [])];
      const idx = forfaits.findIndex((item) =>
        item === editRow
        || (item.idUniteCategorieForfait && editRow.idUniteCategorieForfait?.equals(item.idUniteCategorieForfait))
      );
      const updatedRow = { ...editRow, idPlanEntete };
      if (idx >= 0) {
        forfaits[idx] = updatedRow;
      } else {
        forfaits.unshift(updatedRow);
      }
      return { ...prev, forfaits: normalizeUniteCategorieForfaitRows(forfaits) };
    });
    const rowIndex = editingForfaitRowIndexRef.current;
    if (rowIndex >= 0) {
      forfaitGridRef.current?.instance()?.cellValue(rowIndex, 'idPlanEntete', idPlanEntete);
    }
    setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier, normalizeUniteCategorieForfaitRows]);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  const { descriptionWebOptions, nombrePersonnesExclusiviteOptions, disponibleEnLigneOptions, afficherUnitesOptions, afficherForfaitsPourUnitesOptions, descriptionOptions, ordreAffichageOptions, permettreExclusiviteOptions, obligerExclusiviteOptions, forfaitOrdreAffichageOptions } = useMemo(() => ({
    descriptionWebOptions: { height: 500, maxLength: 3000 },
    nombrePersonnesExclusiviteOptions: { min: 1, showSpinButtons: true, width: '25%' },
    disponibleEnLigneOptions: { text: "Disponible en ligne" },
    afficherUnitesOptions: { text: "Afficher les unités (Au lieu de réserver automatiquement les premières unités disponibles trouvées)" },
    afficherForfaitsPourUnitesOptions: { text: "Utiliser les forfaits de cette catégorie pour ses unités (Sauf si les unités ont leurs propres forfaits définis)" },
    descriptionOptions: { maxLength: 100, width: '60%' },
    ordreAffichageOptions: { min: 1, showSpinButtons: true, width: '25%' },
    permettreExclusiviteOptions: { text: "Permettre l'exclusivité" },
    obligerExclusiviteOptions: { text: "Obliger l'exclusivité" },
    forfaitOrdreAffichageOptions: { min: 0, showSpinButtons: true },
  }), []);

  const forfaits = selectedCategorieUnite?.forfaits ?? [];

  return (
    <FormListBase data={clientFormDataSource}>
      <FormListBaseGrid dataSource={clientFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier)
            setMode(Mode.Lecture);
          if (setSelectedValue && e.row?.data)
            setSelectedValue(e.row.data);
        }}
      >
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-disponible-en-ligne"} dataField={"disponibleEnLigne"} caption="Disponible en ligne" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedCategorieUnite?.description || ''} ${selectedCategorieUnite?.descriptionAnglais || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          <PopupGestionPlanExclusivite
            onSelectionchanged={(data: any) => {
              const idPlanEntete = data?.idPlanEntete || data?.id;
              if (!idPlanEntete) {
                return;
              }
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idPlanExclusivite", idPlanEntete);
            }}
          >
            <GestionPlans isModeSelection={true} />
          </PopupGestionPlanExclusivite>
          <PopupGestionPlansForfaits
            onSelectionchanged={(data: any) => {
              const idPlanEntete = data?.idPlanEntete || data?.id;
              if (!idPlanEntete || !editingUniteCategorieForfaitRowRef.current) {
                return;
              }
              applyForfaitPlanFromPopup(new Guid(String(idPlanEntete)));
            }}
          >
            <GestionPlans isModeSelection={true} />
          </PopupGestionPlansForfaits>
          <TabItem title="Informations">
            <div style={{ maxWidth: '50%' }}>
              <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedCategorieUnite || {} as IGestionCategorieUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                <SimpleItem key={"frm-description"} dataField={"description"}
                  editorOptions={descriptionOptions}>
                  <Label text={"Description française"} />
                </SimpleItem>
                <SimpleItem key={"frm-description-anglais"} dataField={"descriptionAnglais"}
                  editorOptions={descriptionOptions}
                >
                  <Label text={"Description anglaise"} />
                </SimpleItem>
                <SimpleItem key={"frm-ordre-affichage"} dataField={"ordreAffichage"} editorType={"dxNumberBox"}
                  editorOptions={ordreAffichageOptions}>
                  <Label text={"Ordre d'affichage"} />
                </SimpleItem>
                <GroupItem caption={"Exclusivité"}>
                  <GroupItem colCount={2}>
                    <SimpleItem key={"frm-permettre-exclusivite"} dataField={"permettreExclusivite"} editorType={"dxCheckBox"}
                      editorOptions={permettreExclusiviteOptions}
                    >
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem key={"frm-obliger-exclusivite"} dataField={"obligerExclusivite"} editorType={"dxCheckBox"}
                      editorOptions={obligerExclusiviteOptions}
                    >
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <SimpleItem key={"frm-id-plan-exclusivite"} dataField={"idPlanExclusivite"}
                    editorType={"dxSelectBox"}
                    editorOptions={planExclusiviteEditorOption}
                  >
                    <Label text={"Plan exclusivité"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-nombre-personnes-exclusivite"} dataField={"nombrePersonneExclusivite"} editorType={"dxNumberBox"}
                    editorOptions={nombrePersonnesExclusiviteOptions}>
                    <Label text={"Nombre de personnes pour l'exclusivité"} />
                  </SimpleItem>
                </GroupItem>
              </Form>
            </div>
          </TabItem>
          <TabItem title="Forfaits">
            <DataGrid
              ref={forfaitGridRef}
              width={'60%'}
              rowAlternationEnabled={true}
              allowColumnResizing={true}
              showBorders={true}
              repaintChangesOnly={true}
              dataSource={forfaits}
              allowColumnReordering={false}
              filterValue={dataGridFilterValue}
              height={"100%"}
              noDataText={"Aucun forfait"}
              onInitNewRow={(e: any) => {
                e.data.idUniteCategorie = selectedCategorieUnite?.idUniteCategorie;
                e.data.isDeleted = false;
                e.data.idUniteCategorieForfait = uniteCategorieForfaitTempId;
                e.data.actif = e.data.actif ?? true;
                e.data.ordreAffichage = e.data.ordreAffichage ?? 0;
                e.data.disponibleEnLigne = e.data.disponibleEnLigne ?? true;
                e.data.idPlanEntete = e.data.idPlanEntete ?? Guid.empty;
              }}
              onEditorPreparing={(e: any) => {
                if (e.parentType === 'dataRow' && e.dataField === 'idPlanEntete' && e.row?.data) {
                  editingUniteCategorieForfaitRowRef.current = e.row.data;
                  editingForfaitRowIndexRef.current = e.row?.rowIndex ?? -1;
                }
              }}
              onRowInserted={(e) => {
                if (mode !== Mode.Lecture) {
                  syncCategorieUniteForfaitRows(e.component);
                }
              }}
              onRowUpdated={(e) => {
                if (mode === Mode.Lecture) {
                  return;
                }
                if (skipForfaitGridSyncRemainingRef.current > 0) {
                  skipForfaitGridSyncRemainingRef.current--;
                  return;
                }
                syncCategorieUniteForfaitRows(e.component);
              }}
              onRowRemoving={async (e) => {
                e.cancel = true;
                const index = forfaits.findIndex(item =>
                  item === e.data
                  || (item.idUniteCategorieForfait && e.data.idUniteCategorieForfait?.equals(item.idUniteCategorieForfait))
                );
                if (index !== -1) {
                  const updatedData = [...forfaits];
                  updatedData[index] = { ...updatedData[index], isDeleted: true };
                  setSelectedDataRow(prev => (prev ? ({
                    ...prev,
                    forfaits: updatedData
                  }) : prev));
                  setIsFieldModifier(true);
                }
              }}
            >
              <Editing
                mode="cell"
                allowUpdating={mode !== Mode.Lecture}
                allowDeleting={mode !== Mode.Lecture}
                allowAdding={mode !== Mode.Lecture}
                newRowPosition={"first"}
                startEditAction={"click"}
              />
              <Column dataField={"idPlanEntete"} caption="Plan" dataType="string" editorOptions={planForfaitGridLookupOptions}>
                <Lookup {...planForfaitGridLookupOptions} />
              </Column>
              <Column width={75} dataField={"actif"} dataType="boolean" caption="Actif" />
              <Column width={150} dataField={"disponibleEnLigne"} dataType="boolean" caption="Disponible en ligne" />
              <Column width={120} dataField={"ordreAffichage"} dataType="number" caption="Ordre d'affichage" editorOptions={forfaitOrdreAffichageOptions} />
              <Column dataField="isDeleted" dataType="boolean" visible={false} />
            </DataGrid>
          </TabItem>
          <TabItem title="Internet">
            <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedCategorieUnite || {} as IGestionCategorieUnite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem alignItemLabels={false}>
                <SimpleItem key={"frm-disponible-en-ligne"} dataField={"disponibleEnLigne"} editorType={"dxCheckBox"}
                  editorOptions={
                    disponibleEnLigneOptions
                  }
                >
                  <Label visible={false} />
                </SimpleItem>
                <SimpleItem key={"frm-afficher-unites"} dataField={"afficherUnites"} editorType={"dxCheckBox"} editorOptions={afficherUnitesOptions}>
                  <Label visible={false} />
                </SimpleItem>
                <SimpleItem key={"frm-afficher-forfaits-pour-unites"} dataField={"utiliserForfaitsPourUnites"} editorType={"dxCheckBox"} editorOptions={afficherForfaitsPourUnitesOptions}>
                  <Label visible={false} />
                </SimpleItem>
              </GroupItem>
              <GroupItem colCount={2}>
                <SimpleItem key={"frm-description-web"} dataField={"descriptionWeb"} editorType={"dxTextArea"} editorOptions={descriptionWebOptions}>
                  <Label text={"Description web française"} location={"top"} />
                </SimpleItem>
                <SimpleItem key={"frm-description-web-anglais"} dataField={"descriptionWebAnglais"} editorType={"dxTextArea"} editorOptions={descriptionWebOptions}>
                  <Label text={"Description web anglaise"} location={"top"} />
                </SimpleItem>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedCategorieUnite || {} as IGestionCategorieUnite} /></TabItem>
        </FormListBaseTab> : null}
    </FormListBase>
  );
}
